import { t } from "components/CentralDataMangement/translation/Translation";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import React, { useMemo } from "react";
import { AdminFicheFormData } from "../../../annotations/AdminFicheInterface";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import RadioField from "components/common/atoms/RadioField";
import LabelField from "components/common/atoms/LabelField";
import CheckBoxField from "components/common/atoms/CheckBoxField";
import LabelWithTextAreaField from "components/common/molecules/LabelWithTextAreaField";
import Online from "./Online";
import AddExtraData from "./AddExtraData";
import AddPlusIcon from "static/images/AddPlusIcon";
import Button from "components/common/atoms/Button";
import * as CONST from "../../../annotations/AdminFicheConstants";
import Calender from "components/common/molecules/Calender";
import AutoCompleteDropdown from "components/common/atoms/AutoCompleteDropdown";
import { COMPANY_MICROSERVICE } from "Constants";
import { useParams } from "react-router-dom";

interface ChildProps {
  handleChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleSelectChange: (selectedOption: any, title: string) => void;
  state: AdminFicheFormData;
  handleChangeExtraData: (
    value: string | Date | null,
    name: string,
    index: number,
    type: string
  ) => void;
  addAnotherExtraData: (type: string) => void;
  removeExtraData: (index: number, type: string) => void;
  onAddSignatory: () => void;
  dispatch: any;
  handleDateChange: (date: Date | null, name: string) => void;
}

const GeneralDetails: React.FC<ChildProps> = ({
  handleChange,
  handleSelectChange,
  state,
  handleChangeExtraData,
  addAnotherExtraData,
  removeExtraData,
  onAddSignatory,
  dispatch,
  handleDateChange
}) => {
  const generalState = state.general;
  const isPC124 = generalState.paritairBlue.some((pc: any) => pc.value === 39);
  const { agreementId } = useParams<{ agreementId: string }>();

  const selectedSignatory = useMemo(() => {
    const selected = state.signatoryList.find(
      (signatory) => signatory.status === 1
    );
    return selected ? { value: selected.name.trim(), label: selected.name.trim() } : null;
  }, [state.signatoryList]);

  const signatoryOptions = useMemo(() => {
    const existingNames = state.signatoryList.map((s) => s.name.toLowerCase().trim());

    return [
      ...state.signatoryList.map((s) => ({
        value: s.name.trim(),
        label: s.name.trim(),
        type: "signatory",
      })),
      ...state.contactList
        .filter((c) => c?.label)
        .map((c) => {
          const fullName = (c.label || "").toLowerCase().trim();
          
          const contactObj = Object.values(state.contacts).find(contact =>
            `${contact.fName} ${contact.lName}`.toLowerCase().trim() === fullName
          );
          
          return c.label && !existingNames.includes(fullName)
            ? {
                value: c.value,
                label: c.label,
                type: "contact",
                contactData: { 
                  id: c.value, 
                  label: c.label, 
                  functionTitle: contactObj?.functionTitle || "", 
                  virtue: contactObj?.virtue 
                },
              }
            : null;
        })
        .filter((item): item is NonNullable<typeof item> => item !== null),
    ].sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [state.signatoryList, state.contactList, state.contacts]);

  const translatedWorkRegimeOptions = useMemo(() => {
    if (!state.workRegimeList) return [];

    return state.workRegimeList.map((option: any) => ({
      ...option,
      label: t(option.label),
    }));
  }, [state.workRegimeList]);

  const handleSignatorySelection = (selectedSignatory: any) => {
    if (!selectedSignatory) return;

    if (selectedSignatory.type === "contact") {
      // Check if this contact name already exists in signatoryList
      const existingSignatory = state.signatoryList.find(
        (sig) => sig.name.toLowerCase().trim() === selectedSignatory.label.toLowerCase().trim()
      );

      if (existingSignatory) {
        // If already exists, just activate it instead of adding new
        const updatedSignatoryList = state.signatoryList.map((signatory) => ({
          ...signatory,
          status: signatory.name.toLowerCase().trim() === selectedSignatory.label.toLowerCase().trim() ? 1 : 0,
        }));

        dispatch({
          type: CONST.UPDATE_SIGNATORY_LIST,
          value: updatedSignatoryList,
        });
      } else {
        // Only add if doesn't exist
        const contactSignatory = {
          id: null,
          name: selectedSignatory.label,
          position: selectedSignatory.contactData.functionTitle || "",
          pursuant: selectedSignatory.contactData.virtue?.value || 1,
          status: 1,
          contactId: selectedSignatory.contactData.id
        };

        const updatedSignatoryList = [
          ...state.signatoryList.map((signatory) => ({
            ...signatory,
            status: 0,
          })),
          contactSignatory,
        ];

        dispatch({
          type: CONST.UPDATE_SIGNATORY_LIST,
          value: updatedSignatoryList,
        });
      }
    } else {
      // For existing signatories, just update status
      const updatedSignatoryList = state.signatoryList.map((signatory) => ({
        ...signatory,
        status:
          signatory.name === selectedSignatory.label ||
          signatory.name === selectedSignatory.value
            ? 1
            : 0,
      }));

      dispatch({
        type: CONST.UPDATE_SIGNATORY_LIST,
        value: updatedSignatoryList,
      });
    }
  };
  return (
    <>
      <div className="row">
        <div className="col-md-4 autoCompleteFunction">
          {/* <LabelWithInputField
            isMandatory={false}
            name="company"
            label={t("Company")}
            handleChange={(e) => {
              handleChange(e);
            }}
            type="text"
            value={generalState.company}
            error={state.errors.company}
            isDisabled={false}
          /> */}
          <AutoCompleteDropdown
            label={t("Company")}
            placeholder={t("Type to search") + "..."}
            microService={COMPANY_MICROSERVICE}
            onChange={(e) => handleSelectChange(e, "company")}
            value={generalState.company}
            columnName="company_name"
            modelKey="company"
            id="company"
            isMandatory={true}
            name="company"
            filter={true}
            filterInfo={[
              { column: "company_status", value: 1, condition: "where" },
              { column: "company_status", value: 0, condition: "orWhere" }
            ]}
            notNullColumn="bbright_id"
            isDisabled={agreementId ? true : false}
            error={state.errors.company}
          />
        </div>
        <div className="col-md-4 generalTabVAT">
          <div className="input-group mb-3 position-relative">
            <div className="position-absolute fieldPrefix">{generalState.iso || 'BE'}</div>
            <LabelWithInputField
              isMandatory={false}
              name="vatNumber"
              label={t("VAT number")}
              handleChange={handleChange}
              type="text"
              isDisabled={true}
              value={generalState.vatNumber}
            />
          </div>
        </div>
        <div className="col-md-4">
          <SelectWithSearch
            isMulti={false}
            isMandatory={true}
            search={true}
            options={state.agreementList}
            value={generalState.agreementList ?? ""}
            onChange={(e) => {
              handleSelectChange(e, "agreementList");
            }}
            name="agreementList"
            title={t("Agreement List")}
            error={state.errors.vca}
          />
        </div>
        <div className="col-md-4">
          <SelectWithSearch
            isMulti={true}
            isMandatory={false}
            search={true}
            options={[]}
            value={generalState.paritairWhite ?? ""}
            onChange={(e) => {
              handleSelectChange(e, "paritairWhite");
            }}
            name="paritairWhite"
            title={t("PC for white collar")}
            isDisabled={true}
          />
        </div>
        <div className="col-md-4">
          <SelectWithSearch
            isMulti={true}
            isMandatory={false}
            search={true}
            options={[]}
            value={generalState.paritairBlue ?? ""}
            onChange={(e) => {
              handleSelectChange(e, "paritairBlue");
            }}
            isDisabled={true}
            name="paritairBlue"
            title={t("PC for blue collar")}
          />
        </div>
        <div className="col-md-4">
          <SelectWithSearch
            isMulti={false}
            isMandatory={true}
            search={true}
            options={state.vcaList}
            value={generalState.vca ?? ""}
            onChange={(e) => {
              handleSelectChange(e, "vca");
            }}
            name="vca"
            title={t("VCA")}
            error={state.errors.vca}
          />
        </div>
        {isPC124 && (
          <div className="col-md-4">
            <SelectWithSearch
              isMulti={true}
              isMandatory={true}
              search={true}
              options={state.nssoCategoryList}
              value={generalState.nssoCatg ?? ""}
              onChange={(e) => {
                handleSelectChange(e, "nssoCatg");
              }}
              name="nssoCatg"
              title={t("NSSO category")}
              error={state.errors.nssoCatg}
            />
          </div>
        )}
        <div className="col-md-3 marginBotttom1">
          <div className="mb-2">
            <LabelField title={t("Online encodage")} />
          </div>
          <div className="d-inline-block marginRight1">
            <RadioField
              handleChange={handleChange}
              ischecked={generalState.encodage === 1}
              label={t("Yes")}
              value={1}
              name="encodage"
            />
          </div>
          <div className="d-inline-block">
            <RadioField
              handleChange={handleChange}
              ischecked={generalState.encodage === 0}
              label={t("No")}
              value={0}
              name="encodage"
            />
          </div>
        </div>
        <div className="col-md-3 marginBotttom1">
          <div>
            <LabelField title={t("Syndicated delegation")} isMandatory={true} />
          </div>
          <div className="marginRight1 d-inline-block">
            <RadioField
              handleChange={handleChange}
              ischecked={generalState.syndicatedDelig === 1}
              label={t("Yes")}
              value={1}
              name="syndicatedDelig"
            />
          </div>
          <div className="d-inline-block">
            <RadioField
              handleChange={handleChange}
              ischecked={generalState.syndicatedDelig === 0}
              label={t("No")}
              value={0}
              name="syndicatedDelig"
            />
          </div>
          <div>
            <span className="text-danger">{state.errors.syndicatedDelig}</span>
          </div>
        </div>
        {generalState.syndicatedDelig === 1  && 
          <div className="col-md-3 marginBotttom1 position-relative">
            <Calender
              onChange={handleDateChange}
              selectedDate={state.general.syndicatedDeligDate != null ? new Date(state.general.syndicatedDeligDate ): null}
              label={t("Syndicated delegation date")}
              isMandatory={true}
              name="syndicatedDeligDate"
              isDisabled={false}
              error={
                state.errors.syndicatedDeligDate
                  ? state.errors.syndicatedDeligDate.toString()
                  : undefined
              }
            />
          </div>
        }
        <div className="col-md-3 marginBotttom1">
          <div>
            <LabelField title={t("Consecutive daily contracts possible")} />
          </div>
          <div className="d-inline-block marginRight1">
            <RadioField
              handleChange={handleChange}
              ischecked={generalState.isContracts === 1}
              label={t("Yes")}
              value={1}
              name="isContracts"
            />
          </div>
          <div className="d-inline-block">
            <RadioField
              handleChange={handleChange}
              ischecked={generalState.isContracts === 0}
              label={t("No")}
              value={0}
              name="isContracts"
            />
          </div>
        </div>
        {generalState.isContracts === 1 && (
          <div className="col-md-3 marginBotttom1">
            <div>
              <LabelField
                title={t("40 consecutive daily contracts per semester")}
              />
            </div>
            <div className="marginRight1 d-inline-block">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.isContractsPerSem === 1}
                label={t("Yes")}
                value={1}
                name="isContractsPerSem"
              />
            </div>
            <div className="d-inline-block">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.isContractsPerSem === 0}
                label={t("No")}
                value={0}
                name="isContractsPerSem"
              />
            </div>
          </div>
        )}
        {isPC124 && (
          <div className="col-md-3 marginBotttom1">
            <div>
              <LabelField
                title={t("KB 213 (wage supplement 20%) applicable")}
                isMandatory={true}
              />
            </div>
            <div className="d-inline-block marginRight1">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.isKBApplicable === 1}
                label={t("Yes")}
                value={1}
                name="isKBApplicable"
              />
            </div>
            <div className="d-inline-block">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.isKBApplicable === 0}
                label={t("No")}
                value={0}
                name="isKBApplicable"
              />
            </div>
            <div>
              <span className="text-danger">{state.errors.isKBApplicable}</span>
            </div>
          </div>
        )}

        <div className="col-md-3 marginBotttom1">
          <div>
            <LabelField
              title={t("Explicit agreement on work regime(s)")}
              isMandatory={true}
            />
          </div>
          <div className="d-inline-block marginRight1">
            <RadioField
              handleChange={handleChange}
              ischecked={generalState.explicitRegime === 1}
              label={t("Yes")}
              value={1}
              name="explicitRegime"
            />
          </div>
          <div className="d-inline-block">
            <RadioField
              handleChange={handleChange}
              ischecked={generalState.explicitRegime === 0}
              label={t("No")}
              value={0}
              name="explicitRegime"
            />
          </div>
          <div>
            <span className="text-danger">{state.errors.explicitRegime}</span>
          </div>
        </div>
        {generalState.explicitRegime ? (
          <>
            <div className={`col-md-3`}>
              <SelectWithSearch
                isMulti={true}
                isMandatory={true}
                search={true}
                // options={state.workRegimeList}
                options={translatedWorkRegimeOptions}
                value={generalState.exemptionBV ?? ""}
                onChange={(e) => {
                  handleSelectChange(e, "exemptionBV");
                }}
                name="exemptionBV"
                title={t("Work regime(s) exemption BV")}
                error={state.errors.exemptionBV}
                isTranslate={true}
              />
            </div>
            <div className="col-md-3">
              <div className="row">
                <div className="col-10">
                  <SelectWithSearch
                    isMulti={false}
                    isMandatory={false}
                    search={true}
                    options={signatoryOptions}
                    value={selectedSignatory} // Show the currently selected signatory
                    onChange={handleSignatorySelection}
                    name="signatory"
                    title={t("Signatory details")}
                    error={state.errors.signatory}
                  />
                </div>
                <div className="col-2" style={{marginTop: '2vw'}}>
                  <Button
                    className="btn p-0 border-0 table-action-icons"
                    handleClick={onAddSignatory} // Add this line
                  >
                    <AddPlusIcon />
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          ""
        )}
        <div className="col-12" />
        <div
          className="d-grid"
          style={{ gridTemplateColumns: "0.5fr 1fr 1fr", gap: "0 1vw" }}
        >
          <div className="generalOther">
            <LabelField title={t("Other employment agreements")} />
            <div className="marginBottomPoint5">
              <CheckBoxField
                label={t("Large flexibility")}
                name="largeFlex"
                onChangeHandler={handleChange}
                value={generalState.largeFlex ? "1" : "0"}
                isChecked={
                  generalState.largeFlex !== null
                    ? generalState.largeFlex
                    : false
                }
                id="largeFlex"
                lineHeight={"1vw"}
              />
            </div>
            <div className="marginBottomPoint5">
              <CheckBoxField
                label={t("Small flexibility")}
                name="smallFlex"
                onChangeHandler={handleChange}
                value={generalState.smallFlex ? "1" : "0"}
                isChecked={
                  generalState.smallFlex !== null
                    ? generalState.smallFlex
                    : false
                }
                id="smallFlex"
                lineHeight={"1vw"}
              />
            </div>
            <div className="marginBottomPoint5">
              <CheckBoxField
                label={t("Other")}
                name="other"
                onChangeHandler={handleChange}
                value={generalState.other ? "1" : "0"}
                isChecked={
                  generalState.other !== null ? generalState.other : false
                }
                id="other"
                lineHeight={"1vw"}
              />
            </div>
            {generalState.other ? (
              <div className="hideLabel">
                <LabelWithTextAreaField
                  name="otherExtraInfo"
                  handleChange={handleChange}
                  isMandatory={false}
                  value={generalState.otherExtraInfo ?? ""}
                />
              </div>
            ) : (
              ""
            )}
          </div>
          <div>
            <div>
              <LabelField title={t("Catch up rest days")} isMandatory={true} />
            </div>
            <div className="d-block marginBottomPoint5">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.catchUpRest === 1}
                label={t("Free to take (in consultation with customer)")}
                value={1}
                name="catchUpRest"
              />
            </div>
            <div className="d-block marginBottomPoint5">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.catchUpRest === 2}
                label={t("Collectively placed")}
                value={2}
                name="catchUpRest"
              />
            </div>
            <div className="d-block">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.catchUpRest === 3}
                label={t("Combination")}
                value={3}
                name="catchUpRest"
              />
            </div>
            {(generalState.catchUpRest === 2 ||
              generalState.catchUpRest === 3) && (
              <AddExtraData
                extraData={generalState.catchupRestExtraData}
                handleChangeExtraData={handleChangeExtraData}
                addAnotherExtraData={addAnotherExtraData}
                removeExtraData={removeExtraData}
                type={"catchupRestExtraData"}
                error={state.errors.catchupRestExtraData}
              />
            )}
            <div>
              <span className="text-danger">{state.errors.catchUpRest}</span>
            </div>
          </div>
          <div>
            <div>
              <LabelField title={t("Leaves")} isMandatory={true} />
            </div>
            <div className="d-block marginBottomPoint5">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.leaves === 1}
                label={t("Free to take (in consultation with customer)")}
                value={1}
                name="leaves"
              />
            </div>
            <div className="d-block marginBottomPoint5">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.leaves === 2}
                label={t("Collectively placed")}
                value={2}
                name="leaves"
              />
            </div>
            <div className="d-block">
              <RadioField
                handleChange={handleChange}
                ischecked={generalState.leaves === 3}
                label={t("Combination")}
                value={3}
                name="leaves"
              />
            </div>
            {(generalState.leaves === 2 || generalState.leaves === 3) && (
              <AddExtraData
                extraData={generalState.leavesExtraData}
                handleChangeExtraData={handleChangeExtraData}
                addAnotherExtraData={addAnotherExtraData}
                removeExtraData={removeExtraData}
                type={"leavesExtraData"}
                error={state.errors.leavesExtraData}
              />
            )}
            <div>
              <span className="text-danger">{state.errors.leaves}</span>
            </div>
          </div>
        </div>
        <Online handleChange={handleChange} generalState={generalState} />
        <div className={`col-md-12 hideHeight`}>
          <LabelWithTextAreaField
            name="extraInfo"
            handleChange={handleChange}
            label={t("Extra info")}
            isMandatory={false}
            value={generalState.extraInfo}
          />
        </div>
      </div>
    </>
  );
};

export default React.memo(GeneralDetails);
